import React, { useEffect, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import Modal from '../Modal/Modal';
import { useMediaQuery } from '../Layout/shellState';
import { useI18n } from '../../i18n/I18nContext';

/**
 * FilterSheet — the phone pattern for a toolbar with several filters (spec §4): the search box stays
 * in view, and every other filter folds into ONE "Filtrlar" button that shows how many are active
 * and opens a bottom sheet. From `breakpoint` (640px) up there is no button and no sheet: the
 * filters are simply rendered in place, so the toolbar looks exactly as before.
 *
 *   <div className="toolbar">
 *     <SearchInput ... />
 *     <FilterSheet count={activeCount} onClear={resetFilters}>
 *       <Dropdown ... />   <Dropdown ... />   <DateRangePicker ... />
 *     </FilterSheet>
 *   </div>
 *
 * The filters apply as they change (the page owns their state), so the sheet has no Apply step:
 * its footer offers "Filtrlarni tozalash" (only while something is active and `onClear` is given)
 * and "Tayyor". The children render in ONE place at a time (never both inline and in the sheet),
 * so their ids and focus are never duplicated.
 *
 * Props: count (active filters, 0 hides the badge), onClear, title (the sheet's heading; defaults to
 * the button label), breakpoint (px, default 640), className (on the button), children.
 */
function FilterSheet({ count = 0, onClear, title, breakpoint = 640, className = '', children }) {
  const { t } = useI18n();
  const sheet = useMediaQuery(`(max-width: ${breakpoint - 1}px)`, false);
  const [open, setOpen] = useState(false);

  // Widening the window past the breakpoint puts the filters back inline: no sheet may stay open.
  useEffect(() => {
    if (!sheet) setOpen(false);
  }, [sheet]);

  if (!sheet) return <>{children}</>;

  const label = t('ui.filters');
  const active = Number(count) > 0 ? Number(count) : 0;

  return (
    <>
      <button
        type="button"
        className={`ds-btn ds-btn-secondary ui-filter-btn${className ? ` ${className}` : ''}`}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={active ? `${label}, ${t('ui.filtersActive', { count: active })}` : label}
      >
        <SlidersHorizontal aria-hidden="true" />
        <span>{label}</span>
        {active > 0 && <span className="ui-count ui-count--brand" aria-hidden="true">{active}</span>}
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={title || label}
        size="sm"
        footer={(
          <>
            {onClear && active > 0 && (
              <button type="button" className="ds-btn ds-btn-ghost" onClick={onClear}>{t('ui.filtersReset')}</button>
            )}
            <button type="button" className="ds-btn ds-btn-primary" onClick={() => setOpen(false)}>{t('ui.filtersDone')}</button>
          </>
        )}
      >
        <div className="ui-filter-sheet">{children}</div>
      </Modal>
    </>
  );
}

export default FilterSheet;
